import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherBookletsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth();
  const [ownBooklets, setOwnBooklets] = useState([]);
  const [sharedBooklets, setSharedBooklets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBooklet, setSelectedBooklet] = useState(null);
  const [studentEmail, setStudentEmail] = useState('');

  useEffect(() => {
    if (user?.role === 'teacher') {
      fetchBooklets();
    }
  }, []);

  const fetchBooklets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/personalization/teacher-booklets`, {
        withCredentials: true
      });
      setOwnBooklets(response.data.own_booklets || []);
      setSharedBooklets(response.data.shared_booklets || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching booklets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSharing = async (bookletId, currentSharing) => {
    try {
      await axios.post(
        `${API}/personalization/toggle-booklet-sharing`,
        {
          booklet_id: bookletId,
          share: !currentSharing
        },
        { withCredentials: true }
      );
      
      alert(language === 'tr'
        ? `✅ Paylaşım durumu güncellendi`
        : `✅ Sharing status updated`);
      
      fetchBooklets();
    } catch (error) {
      console.error('Error toggling sharing:', error);
      alert(error.response?.data?.detail || (language === 'tr' ? 'Hata oluştu' : 'Error occurred'));
    }
  };

  const handleAssignToStudent = async () => {
    if (!studentEmail.trim()) {
      alert(language === 'tr' ? 'Lütfen öğrenci email adresi girin' : 'Please enter student email');
      return;
    }

    try {
      // First, find student by email
      const studentResponse = await axios.get(
        `${API}/users/search?email=${studentEmail}`,
        { withCredentials: true }
      );

      if (!studentResponse.data.user) {
        alert(language === 'tr' ? 'Öğrenci bulunamadı' : 'Student not found');
        return;
      }

      // Assign booklet
      await axios.post(
        `${API}/personalization/assign-booklet`,
        {
          booklet_id: selectedBooklet.id,
          student_ids: [studentResponse.data.user.id]
        },
        { withCredentials: true }
      );

      alert(language === 'tr'
        ? `✅ Kitapçık ${studentEmail} adresine atandı`
        : `✅ Booklet assigned to ${studentEmail}`);
      
      setShowAssignModal(false);
      setStudentEmail('');
      setSelectedBooklet(null);
      fetchBooklets();
    } catch (error) {
      console.error('Error assigning booklet:', error);
      alert(error.response?.data?.detail || (language === 'tr' ? 'Atama başarısız' : 'Assignment failed'));
    }
  };

  const handleDeleteBooklet = async (bookletId) => {
    if (!window.confirm(language === 'tr' 
      ? 'Bu kitapçığı silmek istediğinizden emin misiniz?' 
      : 'Are you sure you want to delete this booklet?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/personalization/teacher-booklet/${bookletId}`,
        { withCredentials: true }
      );

      alert(language === 'tr' ? '✅ Kitapçık silindi' : '✅ Booklet deleted');
      fetchBooklets();
    } catch (error) {
      console.error('Error deleting booklet:', error);
      alert(language === 'tr' ? '❌ Silme başarısız' : '❌ Delete failed');
    }
  };

  if (user?.role !== 'teacher' && user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {language === 'tr' ? 'Bu özellik sadece öğretmenler için' : 'This feature is for teachers only'}
          </h1>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Kitapçıklarım' : 'My Booklets'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {language === 'tr' ? '📚 Kitapçıklarım' : '📚 My Booklets'}
              </h1>
              <p className="text-gray-600">
                {language === 'tr'
                  ? 'Oluşturduğunuz ve size paylaşılan kitapçıkları yönetin'
                  : 'Manage your created and shared booklets'}
              </p>
            </div>
            
            <button
              onClick={() => navigate('/create-booklet-from-bank')}
              className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-all shadow-lg"
            >
              ➕ {language === 'tr' ? 'Yeni Kitapçık' : 'New Booklet'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="text-4xl mb-2">🔒</div>
            <div className="text-3xl font-bold">{stats.private_count || 0} / {stats.private_limit || 10}</div>
            <p className="opacity-90">{language === 'tr' ? 'Özel Kitapçık' : 'Private Booklets'}</p>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <div className="text-4xl mb-2">🌐</div>
            <div className="text-3xl font-bold">{stats.shared_count || 0}</div>
            <p className="opacity-90">{language === 'tr' ? 'Paylaşılan' : 'Shared'}</p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 text-white">
            <div className="text-4xl mb-2">📥</div>
            <div className="text-3xl font-bold">{sharedBooklets.length}</div>
            <p className="opacity-90">{language === 'tr' ? 'Alınan Paylaşım' : 'Received Shares'}</p>
          </div>
        </div>

        {/* Own Booklets */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? 'Benim Kitapçıklarım' : 'My Booklets'}
          </h2>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : ownBooklets.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{language === 'tr' ? 'Henüz kitapçık oluşturmadınız' : 'No booklets created yet'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ownBooklets.map((booklet) => (
                <div key={booklet.id} className="border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-gray-900 flex-1">{booklet.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      booklet.visibility === 'shared'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {booklet.visibility === 'shared' 
                        ? (language === 'tr' ? '🌐 Paylaşıldı' : '🌐 Shared')
                        : (language === 'tr' ? '🔒 Özel' : '🔒 Private')}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">
                    {booklet.questions?.length || 0} {language === 'tr' ? 'soru' : 'questions'}
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setSelectedBooklet(booklet);
                        setShowAssignModal(true);
                      }}
                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                    >
                      {language === 'tr' ? '📤 Ata' : '📤 Assign'}
                    </button>
                    
                    <button
                      onClick={() => handleToggleSharing(booklet.id, booklet.shared_with_teachers)}
                      className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition"
                    >
                      {booklet.shared_with_teachers
                        ? (language === 'tr' ? '🔒 Gizle' : '🔒 Make Private')
                        : (language === 'tr' ? '🌐 Paylaş' : '🌐 Share')}
                    </button>
                    
                    <button
                      onClick={() => handleDeleteBooklet(booklet.id)}
                      className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-red-200 transition"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Shared Booklets */}
        {sharedBooklets.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'Benimle Paylaşılan Kitapçıklar' : 'Booklets Shared With Me'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sharedBooklets.map((booklet) => (
                <div key={booklet.id} className="border-2 border-green-200 rounded-xl p-4 bg-green-50">
                  <div className="mb-3">
                    <h3 className="font-bold text-gray-900">{booklet.title}</h3>
                    <p className="text-xs text-gray-500">
                      {language === 'tr' ? 'Oluşturan:' : 'By:'} {booklet.created_by_name}
                    </p>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">
                    {booklet.questions?.length || 0} {language === 'tr' ? 'soru' : 'questions'}
                  </p>
                  
                  <button
                    onClick={() => {
                      setSelectedBooklet(booklet);
                      setShowAssignModal(true);
                    }}
                    className="w-full bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition"
                  >
                    {language === 'tr' ? '📤 Öğrenciye Ata' : '📤 Assign to Student'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Assign Modal */}
      {showAssignModal && selectedBooklet && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setShowAssignModal(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'Öğrenciye Ata' : 'Assign to Student'}
            </h2>

            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                <strong>{selectedBooklet.title}</strong>
              </p>
              
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {language === 'tr' ? 'Öğrenci Email Adresi' : 'Student Email Address'}
              </label>
              <input
                type="email"
                value={studentEmail}
                onChange={(e) => setStudentEmail(e.target.value)}
                placeholder={language === 'tr' ? 'ornek@email.com' : 'example@email.com'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold transition"
              >
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
              <button
                onClick={handleAssignToStudent}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
              >
                {language === 'tr' ? '✅ Ata' : '✅ Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherBookletsPage;
